/** Local calendar arithmetic avoids UTC date keys and 24-hour DST assumptions. */
import { formatDate } from "@/i18n";
import type en from "@/i18n/messages/frames-taskboard/en";

export const TASK_DATE_GROUPS = [
  "overdue",
  "today",
  "tomorrow",
  "this-week",
  "next-week",
  "later",
  "no-date",
] as const;
export type TaskDateGroup = (typeof TASK_DATE_GROUPS)[number];
/** Catalogue key of each group's header in `framesTaskboardMessages`. */
export const TASK_DATE_GROUP_LABEL_KEYS = {
  overdue: "overdue",
  today: "today",
  tomorrow: "tomorrow",
  "this-week": "thisWeek",
  "next-week": "nextWeek",
  later: "later",
  "no-date": "noDate",
} as const satisfies Record<TaskDateGroup, keyof typeof en>;
export function localDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
export function taskDueDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly
    ? new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      )
    : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}
export function taskDateGroup(
  value: string | null | undefined,
  now: Date,
): TaskDateGroup {
  const due = taskDueDate(value);
  if (!due) return "no-date";
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const afterTomorrow = new Date(today);
  afterTomorrow.setDate(afterTomorrow.getDate() + 2);
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + (8 - (today.getDay() || 7)));
  const later = new Date(nextWeek);
  later.setDate(later.getDate() + 7);
  if (day < today) return "overdue";
  if (day < tomorrow) return "today";
  if (day < afterTomorrow) return "tomorrow";
  if (day < nextWeek) return "this-week";
  if (day < later) return "next-week";
  return "later";
}
export function shortTaskDueDate(
  value: string | null | undefined,
  now = new Date(),
): string | undefined {
  const group = taskDateGroup(value, now);
  const date = taskDueDate(value);
  return !date || group === "today" || group === "tomorrow"
    ? undefined
    : formatDate(date, { month: "short", day: "numeric" });
}
/** The Monday that starts `date`'s week, at local midnight. */
function weekStart(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() - ((date.getDay() + 6) % 7),
  );
}
/**
 * Where the calendar opens: on upcoming work. The anchor is the next due
 * day from `today` on, else today. The month is the anchor's, or the next
 * one when the anchor's week runs into it (that month's grid starts with
 * the week). `topDay` starts the row to scroll to the top: the current
 * week's when the grid holds it, else the anchor's.
 */
export function calendarOpening(
  dueDates: Iterable<Date>,
  today: Date,
): { month: Date; topDay: Date } {
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  let next: Date | null = null;
  for (const due of dueDates) {
    const day = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    if (day >= start && (!next || day < next)) next = day;
  }
  const anchorWeek = weekStart(next ?? start);
  const weekEnd = new Date(
    anchorWeek.getFullYear(),
    anchorWeek.getMonth(),
    anchorWeek.getDate() + 6,
  );
  const month = new Date(weekEnd.getFullYear(), weekEnd.getMonth(), 1);
  const currentWeek = weekStart(start);
  return {
    month,
    topDay: currentWeek >= weekStart(month) ? currentWeek : anchorWeek,
  };
}
export function calendarMonthDays(month: Date): Date[] {
  const start = weekStart(new Date(month.getFullYear(), month.getMonth(), 1));
  return Array.from(
    { length: 42 },
    (_, index) =>
      new Date(start.getFullYear(), start.getMonth(), start.getDate() + index),
  );
}
